import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import CustomDatePicker from 'Components/CustomDatePicker'
import ListActions from 'Components/ListActions'
import PageTitle from 'Components/PageTitle'
import SearchByField from 'Components/SearchByField'
import StyledAccordion from 'Components/StyledAccordion'
import { useGetTicketSearchFieldOptionsQuery } from 'Services/SalesManagement/Features/AffiliateProduct/affiliateProductApi'
import {
  fetchTicketList,
  type TicketParamShape
} from 'Services/SalesManagement/Features/AffiliateProduct/affiliateProductSlice'
import { TicketList } from 'Services/SalesManagement/Features/AffiliateProduct/Components/TicketList'
import useTypedSelector, { useAppDispatch } from 'Store'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { dateFormatter } from 'Util/formatters'
import useQuery from 'Util/getQueryParamsHook'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FilterListIcon from '@mui/icons-material/FilterList'
import { AccordionDetails, AccordionSummary, Button, Divider } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

export default function TicketListPage() {
  const title = t`Transfer management`
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const queryParams = useQuery()

  const { tickets, count, loading } = useTypedSelector(state => state.affiliateProduct)

  const { data: ticketSearchFieldChoices, isFetching: isTicketSearchFieldChoicesFetching } =
    useGetTicketSearchFieldOptionsQuery()

  const rows = tickets

  const defaultSearchFieldChoice = ticketSearchFieldChoices?.search_field_choices
    ? Object.entries(ticketSearchFieldChoices?.search_field_choices)[0][0]
    : ''
  const initialSearchFieldChoice = queryParams.get('search_fields') ?? defaultSearchFieldChoice
  const [searchText, setSearchText] = useState(queryParams.get('search') ?? '')
  const [searchField, setSearchField] = useState(initialSearchFieldChoice)

  const filterByDefaultDate = new Date()
  filterByDefaultDate.setDate(filterByDefaultDate.getDate() - 30)

  const [filters, setFilters] = useState<Partial<TicketParamShape>>({
    created_after: queryParams.get('created_after') ?? dateFormatter(filterByDefaultDate),
    created_before: queryParams.get('created_before') ?? ''
  })

  const [params, setParams] = useState<TicketParamShape>({
    ordering: queryParams.get('ordering') ?? '-external_creation_date',
    page: queryParams.get('page') ? parseInt(queryParams.get('page') as string) : 1,
    created_after: queryParams.get('created_after') ?? dateFormatter(filterByDefaultDate),
    created_before: queryParams.get('created_before') ?? '',
    search: queryParams.get('search') ?? '',
    search_fields: initialSearchFieldChoice
  })

  useEffect(() => {
    dispatch(fetchTicketList(params))
      .then(unwrapResult)
      .then(() => {
        setQueryParams()
      })
  }, [dispatch, params])

  useEffect(() => {
    setSearchField(initialSearchFieldChoice)
  }, [ticketSearchFieldChoices])

  const setQueryParams = () => {
    const query: string[] = []
    Object.keys(params).forEach((key: string) => {
      const value = params[key as keyof TicketParamShape]
      if (value) {
        query.push(key + '=' + value)
      }
    })
    const queryString = query.join('&')

    navigate({
      pathname: location.pathname,
      search: queryString
    })
  }

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = params.ordering.includes('-')
    setParams(prevState => ({ ...prevState, ordering: `${isAsc ? '' : '-'}${property}` }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({ ...prevState, page: newPage + 1 }))
  }

  const handleSearch = () => {
    setParams(prevState => ({
      ...prevState,
      search: searchText,
      search_fields: searchField,
      page: 1
    }))
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleSearchFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setSearchField(value)
  }

  const handleDatePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = dateFormatter(value).toString()
    }
    setFilters(prevState => ({ ...prevState, [name]: formattedValue }))
  }

  const submitFilters = () => {
    setParams(prevState => ({ ...prevState, ...filters, page: 1 }))
  }

  const clearFilters = () => {
    setFilters(prevState => ({
      ...prevState,
      created_after: '',
      created_before: ''
    }))
  }

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SALES_MANAGEMENT_ROOT_URL
    },
    {
      title: title
    }
  ]

  const searchFieldChoices = Object.entries(ticketSearchFieldChoices?.search_field_choices ?? {})

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <BackButton link={`${SALES_MANAGEMENT_ROOT_URL}/`} />
        <PageTitle>{title}</PageTitle>
        <Typography component="p" variant="subtitle1" sx={{ fontSize: '15px', mb: 4 }}>
          {t`In the transfer management section you can search for tickets and manage their maximum transfer amount. Management of maximum transfer amount and viewing of transfer history can be accessed by clicking the external ID on the ticket's row. Note that by default the tickets are filtered by the last 30 days.`}
        </Typography>
        <ListActions>
          <SearchByField
            searchText={searchText}
            handleSearchTextChange={handleSearchTextChange}
            handleSearch={handleSearch}
            searchLabelText={t`Search for tickets`}
            searchHelperText={t`Search for ticket ID, email address or Waltti ID`}
            searchField={searchField}
            isSearchFieldChoicesLoading={isTicketSearchFieldChoicesFetching}
            searchFieldChoices={searchFieldChoices}
            handleSearchFieldChange={handleSearchFieldChange}
          />
        </ListActions>
      </Box>
      <StyledAccordion sx={{ mb: 3 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <FilterListIcon sx={{ mr: 1 }} color="primary" />
          <Typography component="span">{t`Filters`}</Typography>
        </AccordionSummary>
        <Divider />

        <AccordionDetails>
          <Box sx={{ mb: 3 }}>
            <Typography
              component="h3"
              variant="h3"
              sx={{ my: 2 }}
            >{t`Filter by time range`}</Typography>
            <Box sx={{ display: 'flex', mb: 6 }}>
              <CustomDatePicker
                onChange={handleDatePickerChange}
                name="created_after"
                value={filters.created_after ? new Date(filters.created_after) : null}
                dateToCompare={filters.created_before ? new Date(filters.created_before) : null}
                dateToCompareFieldName="created_before"
                isStartDate={true}
                label={t`Start date`}
              />
              <Box sx={{ p: 2 }}>{` - `}</Box>
              <CustomDatePicker
                onChange={handleDatePickerChange}
                name="created_before"
                value={filters.created_before ? new Date(filters.created_before) : null}
                dateToCompare={filters.created_after ? new Date(filters.created_after) : null}
                dateToCompareFieldName="created_after"
                isStartDate={false}
                label={t`End date`}
              />
            </Box>
            <Box sx={{ mt: 3 }}>
              <CustomButton sx={{ mr: 2 }} onClick={submitFilters}>{t`Filter`}</CustomButton>
              <Button onClick={clearFilters}>{t`Clear filters`}</Button>
            </Box>
          </Box>
        </AccordionDetails>
      </StyledAccordion>
      <TicketList
        rows={rows}
        loading={loading}
        params={params}
        handleRequestSort={handleRequestSort}
        handleChangePage={handleChangePage}
        count={count}
      />
    </Box>
  )
}
